import { Alert, Flex } from "antd"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues"
import { theme } from "src/global/ui/themes/theme"
import { BodyNormal } from "src/global/ui/typography/Text/Text"
import { AnomalyDetectionCoverageStats } from "src/api/graphql/graphql"
import { AlertBanner } from "src/global/ui/Alert"
import { ApolloError } from "@apollo/client"

export const anomalyDetectionContent = {
  Schema: {
    paragraph:
      "Use GX Cloud’s Anomaly Detection algorithm for schema. This Expectation will fail if there’s a deviation from the observed pattern. For example, the addition or removal of columns for this Data Asset.",
    alertMessage: {
      success: () => "Great news! GX Cloud is monitoring this Data Asset’s schema.",
    },
  },
  Volume: {
    paragraph:
      "Use GX Cloud’s Anomaly Detection algorithm for volume, which continuously learns from your Data Asset’s row count. This Expectation will fail if there’s a deviation from the observed pattern. For example, a sudden increase when volume has been stable or stagnation in a season when volume typically grows.",
    alertMessage: {
      success: () => "Great news! GX Cloud is monitoring this Data Asset’s volume.",
    },
  },
  Completeness: {
    paragraph:
      "Use GX Cloud’s Anomaly Detection algorithm for completeness, which continuously learns from your Data Asset’s percentage of null values. This Expectation will fail if there’s a deviation from the observed pattern. For example, a sudden increase or decrease in percentage of null values in a particular column.",
    alertMessage: {
      success: () => "Great news! GX Cloud is monitoring this Data Asset’s completeness across all columns.",
      warning: (anomalyDetectionCoverageStats: AnomalyDetectionCoverageStats | null | undefined) => {
        const covered = anomalyDetectionCoverageStats
          ? anomalyDetectionCoverageStats.numColumnsWithCompletenessCoverage
          : 0
        const total = anomalyDetectionCoverageStats ? anomalyDetectionCoverageStats.totalColumnCount : 0
        return `GX Cloud is monitoring this Data Asset's completeness in ${covered}/${total} columns. Click "Save" to add Anomaly Detection for the remaining columns.`
      },
    },
  },
} as const

type AnomalyDetectionQualityIssues = keyof typeof anomalyDetectionContent

export function isAnomalyDetectionQualityIssue(
  issueType: DataQualityIssue | undefined,
): issueType is AnomalyDetectionQualityIssues {
  return issueType ? issueType in anomalyDetectionContent : false
}

type AutomaticExpectationSuccessAlertProps = {
  issueType: DataQualityIssue
}

function AutomaticExpectationSuccessAlert({ issueType }: AutomaticExpectationSuccessAlertProps) {
  if (!isAnomalyDetectionQualityIssue(issueType)) return null
  const message = anomalyDetectionContent[issueType].alertMessage.success()

  return <Alert type="success" message={message} showIcon style={{ marginBottom: theme.spacing.s }} />
}

type AutomaticExpectationWarningAlertProps = {
  issueType: DataQualityIssue
  anomalyDetectionCoverageStats: AnomalyDetectionCoverageStats | null | undefined
}

function AutomaticExpectationWarningAlert({
  issueType,
  anomalyDetectionCoverageStats,
}: AutomaticExpectationWarningAlertProps) {
  if (!isAnomalyDetectionQualityIssue(issueType)) return null
  if (!("warning" in anomalyDetectionContent[issueType].alertMessage)) return null
  const message = anomalyDetectionContent[issueType].alertMessage.warning(anomalyDetectionCoverageStats)

  return <Alert type="warning" message={message} showIcon style={{ marginBottom: theme.spacing.s }} />
}

type AutomaticExpectationEditorProps = {
  issueType: DataQualityIssue
  anomalyDetectionCoverageStats?: AnomalyDetectionCoverageStats | null
  addAutomaticExpectationToAssetError?: ApolloError
}

export function AutomaticExpectationEditor({
  issueType,
  anomalyDetectionCoverageStats,
  addAutomaticExpectationToAssetError,
}: AutomaticExpectationEditorProps) {
  if (!isAnomalyDetectionQualityIssue(issueType)) {
    return null
  }

  return (
    <Flex vertical justify="flex-start" style={{ marginTop: theme.spacing.s }}>
      {isDataQualityIssueFullyCovered(issueType, anomalyDetectionCoverageStats) ? (
        <AutomaticExpectationSuccessAlert issueType={issueType} />
      ) : (
        <AutomaticExpectationWarningAlert
          issueType={issueType}
          anomalyDetectionCoverageStats={anomalyDetectionCoverageStats}
        />
      )}
      <BodyNormal>{anomalyDetectionContent[issueType].paragraph}</BodyNormal>
      {addAutomaticExpectationToAssetError && (
        <AlertBanner
          message={`An error occurred while applying ${issueType} Anomaly Detection coverage.`}
          description={addAutomaticExpectationToAssetError.message}
        />
      )}
    </Flex>
  )
}

export function isDataQualityIssueFullyCovered(
  selectedDataQualityIssue: DataQualityIssue | undefined,
  anomalyDetectionCoverageStats: AnomalyDetectionCoverageStats | undefined | null,
) {
  if (selectedDataQualityIssue === "Completeness") {
    return anomalyDetectionCoverageStats?.hasCompletenessCoverage ?? false
  }
  if (selectedDataQualityIssue === "Schema") {
    return anomalyDetectionCoverageStats?.hasSchemaCoverage ?? false
  }
  if (selectedDataQualityIssue === "Volume") {
    return anomalyDetectionCoverageStats?.hasVolumeCoverage ?? false
  }
  return false
}
